import { Tab, Loader, DocumentUploadDialog } from '@/ui'
import { useReselector, useFlyoutV2 } from '@/utils/sharedHooks'
import {
  getUserTransactionById,
  selectTransactionSplitInfoById,
} from '@/features/transactions/transactions.selectors'
import { DetailsPanel } from './DetailsPanel'
import { NotesPanel } from './NotesPanel'
import { UploadDocumentType } from '@/constants/businessConstants'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { useState } from 'react'

interface TransactionDetailsProps {
  transactionId: number
  parentId?: number
  isManualTransaction?: boolean
  defaultActiveIndex?: number
  setPreventClose: (value: boolean) => void
}

export const TransactionDetails = ({
  transactionId,
  parentId,
  isManualTransaction,
  defaultActiveIndex = 0,
  setPreventClose,
}: TransactionDetailsProps) => {
  const [fileModalOpen, setFileModalOpen] = useState(false)
  const { closeFlyout } = useFlyoutV2()

  const splitInfo = useReselector(
    selectTransactionSplitInfoById,
    transactionId,
    parentId
  )

  const transactionIdToFind = parentId || transactionId
  const transaction = useReselector(
    getUserTransactionById,
    transactionIdToFind,
    splitInfo?.splitIndex
  )

  const { uploadDocuments } = useDocumentUpload({
    documentType: UploadDocumentType.RECEIPT,
    extraPayload: { transactionId: Number(transaction?.id) },
    onUploadSuccess: () => {
      // Reset preventClose first, then close flyout
      setPreventClose(false)
      // Use setTimeout to ensure preventClose state is updated before closing
      setTimeout(() => {
        closeFlyout()
      }, 0)
    },
  })

  const toggleFileModal = () => setFileModalOpen(!fileModalOpen)

  if (!transaction) return <Loader active inline />

  return (
    <>
      <Tab
        className="transaction-details-tabs"
        defaultActiveIndex={defaultActiveIndex}
        panes={[
          {
            menuItem: 'Details',
            render: () => (
              <DetailsPanel
                transaction={transaction}
                splitIndex={splitInfo?.splitIndex}
                totalSplits={splitInfo?.totalSplits}
                isManualTransaction={isManualTransaction}
                setPreventClose={setPreventClose}
              />
            ),
          },
          {
            menuItem: 'Notes & Receipts',
            render: () => (
              <NotesPanel
                transaction={transaction}
                toggleFileModal={toggleFileModal}
                splitIndex={splitInfo?.splitIndex}
                parentId={parentId}
                setPreventClose={setPreventClose}
              />
            ),
          },
        ]}
      />

      <DocumentUploadDialog
        isOpen={fileModalOpen}
        onOpenChange={setFileModalOpen}
        onClose={() => {
          setFileModalOpen(false)
          setPreventClose(false)
        }}
        onUpload={(files) => {
          uploadDocuments(files)
        }}
        cardTitle="Upload Receipt"
        cardDescription="Upload a receipt or document for this transaction"
      />
    </>
  )
}
